/**
 * How a stuck client reaches a human.
 *
 * Five places in this app tell someone to ring us — the `<noscript>` block, the sign-in screen, the
 * error boundary, the unreachable-portal screen and the no-dogs card. Before this file existed, not
 * one of them carried a number: the instruction was "please ring us" and then nothing. Somebody
 * locked out of their record was told to phone a business whose number the page declined to give.
 *
 * So the number lives here once. Every site imports it, which is what stops the five drifting and
 * what makes changing it a one-line job rather than a grep.
 *
 * DELIBERATELY PLAIN DATA, NO JSX. `vite.config.ts` imports this module to substitute the values
 * into `index.html` at build time — the `<noscript>` block is static HTML that cannot import a
 * React module, and hard-coding the number there would recreate exactly the drift this file exists
 * to prevent. A `.tsx` export would drag the React runtime into the Vite config; keep it `.ts`.
 *
 * Source of the values: the contact page of thefairytails.co.uk, which is what a client would find
 * if they went looking anyway. The main line is the one to publish here — the out-of-hours mobile
 * on that page is for genuine emergencies with a dog in our care, not for a portal login problem,
 * and putting it on a sign-in screen would route password trouble to an emergency phone.
 */

/** As a human should read it aloud and as it appears on our own website. */
export const PHONE_DISPLAY = '01424 300668'

/**
 * E.164 for the `tel:` href — no spaces, leading `0` replaced by `+44`.
 *
 * Kept separate from `PHONE_DISPLAY` on purpose: a `tel:` link built from the spaced form works on
 * most handsets and silently fails on some, and a client who taps a number that does nothing has
 * been told twice that they are on their own.
 */
export const PHONE_TEL = '+441424300668'

/** A real, monitored mailbox. Replies to sign-in mail are pointed here too. */
export const SUPPORT_EMAIL = 'info@thefairytails.co.uk'

/** The `href` for a phone link, so no call site has to remember the `tel:` scheme. */
export const PHONE_HREF = `tel:${PHONE_TEL}`
